import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, filter, lastValueFrom, map, Observable } from "rxjs";
import { IAirline, IAirlineSettings } from "../../types/airline";
import { AirlineApiService } from "../apiservices/airlineapi.service";
import { CookieService } from "./cookie.service";
import { StorageService } from "./storageService.service";

@Injectable({ providedIn: 'root' })
export class AirlineService {

  private _airlines: IAirline[];
  private _api = inject(AirlineApiService);
  private _storage = inject(StorageService);
  private _cookies = inject(CookieService);

  public getAirlineList(grantedTenants: string[]): Promise<IAirline[]> {
    const request = this._api.getAirlineList().pipe(map(this._filterGrantedTenants(grantedTenants)));
    return lastValueFrom(request);
  }

  /**
   * filters and returns only the airlines granted to the current user.
   */
  private _filterGrantedTenants(grantedTenants: string[]) {
    return ((airlines: IAirline[]): IAirline[] => {
      this._airlines = airlines.reduce((acc, airline) => {
        if (!airline.code || !grantedTenants.includes(airline.code)) return acc;
        acc.push({ ...airline, name: airline.name || airline.code });
        return acc;
      }, [] as IAirline[]);
      return this._airlines;
    });
  }

  //#region [main] Current Airline

  private readonly _currentAirlineSubject = new BehaviorSubject<IAirline>(null);
  public readonly currentAirlineChange = this._currentAirlineSubject.asObservable();

  public get currentAirline(): IAirline {
    return this._airlines ? this._tryToGetCurrentTenant(this._airlines) : null;
  }

  public set currentAirline(value: IAirline) {
    this._storage.set.currentAirline(value);
    this._currentAirlineSubject.next(value);
  }

  public observeTenantChange(): Observable<IAirline> {
    return this._cookies.observe.currentTenant()
      .pipe(
        map((code) => {
          code = code.toLowerCase();
          if (code == this._currentAirlineSubject.value.code) return null;
          const airline = this._airlines.find(airline => airline.code == code);
          return airline || null;
        }),
        filter(airline => !!airline)
      );
  }

  private _tryToGetCurrentTenant(tenants: IAirline[]): IAirline | null {

    const cookieTenantCode = this._cookies.get.currentTenant()?.toLowerCase();
    const localTenant = this._storage.get.currentAirline();

    if (cookieTenantCode != localTenant?.code) {

      const cookieTenant = tenants.find((tenant) => tenant.code == cookieTenantCode);
      if (cookieTenant) {
        this.currentAirline = cookieTenant;
        return cookieTenant;
      }

    }

    if (localTenant) {
      const finalTenant = tenants.find(tenant => tenant.code == localTenant.code);
      const subjectTenant = this._currentAirlineSubject.value;
      if (finalTenant?.code != subjectTenant?.code) this._currentAirlineSubject.next(finalTenant);
      return finalTenant;
    }
    else
      return null;
  }

  //#endregion

  /**
   * Returns the airline settings given an Id
   */
  public getAirlineSettings(): Promise<IAirlineSettings> {
    const request = this._api.getAirlineSettings();
    return lastValueFrom(request);
  }

  /**
   * Return an airline object with id, code and name as keys
   */
  public getAirlineById(airlineId: string = ':airline_id:'): Promise<IAirline> {
    const request = this._api.getAirlineSettings(airlineId).pipe(map(this._mapAirline()));
    return lastValueFrom(request);
  }

  private _mapAirline(): (settings: IAirlineSettings) => IAirline {
    return (settings: IAirlineSettings) => ({
      id: settings.id,
      code: settings.identifier,
      name: settings.name,
      standard_airTRFX: settings.standard_airTRFX
    });
  }

  /**
   * Returns the airline languages given an airline id
   */
  public getAirlineLanguages(airlineId: string = ':airline_id:'): Promise<{ languages: string[] }> {
    const request = this._api.getAirlineLanguages(airlineId);
    return lastValueFrom(request);
  }
}
